// ============================================================
// CatalogPicker - search the catalog, add anything.
//
// Opens under a section's caption. Type to narrow the catalog by name,
// id or description; a row adds its entry, and stays there marked
// "taken" unless it may be taken again. What no catalog knows can be
// written down as a custom entry from the same box. Enter adds the first
// entry whose name holds what was typed (a custom entry when none does),
// Escape closes.
//
// Modelled on SpecialsPicker (search, then the catalog under small
// family headings), which the table already knows from the armoury.
// ============================================================
import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { Character } from '../../engine/character/types';
import { KINDS, type CatalogEntry, type InstanceKind } from './instanceKinds';
import styles from './Sheet.module.css';

interface Props {
  kind: InstanceKind;
  character: Character;
  onChange: (partial: Partial<Character>) => void;
  onClose: () => void;
}

const matches = (entry: CatalogEntry, needle: string) =>
  !needle || [entry.name, entry.id, entry.description].some((text) => text.toLowerCase().includes(needle));

/** The entries that match, under their group headings, in catalog order. */
function grouped(entries: CatalogEntry[], needle: string): [string, CatalogEntry[]][] {
  const groups = new Map<string, CatalogEntry[]>();
  for (const entry of entries.filter((e) => matches(e, needle))) {
    groups.set(entry.group, [...(groups.get(entry.group) ?? []), entry]);
  }
  return [...groups.entries()];
}

/** The first entry that can be added and whose name holds what was typed. */
function firstNamed(entries: CatalogEntry[], needle: string, canAdd: (id: string) => boolean) {
  return entries.find((entry) => entry.name.toLowerCase().includes(needle) && canAdd(entry.id));
}

interface RowProps extends Omit<Props, 'onClose'> {
  entry: CatalogEntry;
  taken: boolean;
}

function Row({ kind, character, onChange, entry, taken }: RowProps) {
  const api = KINDS[kind];
  const addable = api.canAdd(character, entry.id);
  const tag = entry.coverageTag;
  return (
    <button
      type="button"
      className={styles.pickerRow}
      data-testid={`${kind}-add-${entry.id}`}
      disabled={!addable}
      onClick={() => onChange(api.add(character, entry.id))}
    >
      <span className={styles.pickerName}>
        {entry.name}
        {entry.parameterized && <i className={styles.chipTag}>({entry.parameterLabel ?? 'X'})</i>}
        {tag && <i className={styles.chipTag}>{tag}</i>}
        {taken && <i className={styles.pickerTaken}>{addable ? 'add another' : 'taken'}</i>}
      </span>
      <span className={styles.pickerDesc}>{entry.description}</span>
    </button>
  );
}

interface GroupProps extends Omit<Props, 'onClose'> {
  group: string;
  entries: CatalogEntry[];
  taken: Set<string>;
}

/** One family of entries under its heading. */
function Group({ kind, character, onChange, group, entries, taken }: GroupProps) {
  return (
    <div>
      <div className={styles.readoutLabel}>{group}</div>
      {entries.map((entry) => (
        <Row key={entry.id} {...{ kind, character, onChange, entry }} taken={taken.has(entry.id)} />
      ))}
    </div>
  );
}

export default function CatalogPicker({ kind, character, onChange, onClose }: Props) {
  const api = KINDS[kind];
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const groups = grouped(api.entries, needle);
  const taken = new Set(api.instancesOf(character).map((instance) => instance.id));
  const known = api.entries.some((entry) => entry.name.toLowerCase() === needle);
  const addCustom = () => {
    onChange(api.addCustom(character, query.trim()));
    setQuery('');
  };
  // Enter means what was TYPED: the first entry whose NAME holds it, else a
  // custom entry of that name. Never the top of an unfiltered catalog, and
  // never an entry that only matched in its description.
  const addTyped = () => {
    const named = needle && firstNamed(api.entries, needle, (id) => api.canAdd(character, id));
    if (named) onChange(api.add(character, named.id));
    else if (needle && !known) addCustom();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') addTyped();
    if (e.key !== 'Escape') return;
    // Stop here: the same key would otherwise close whatever the sheet sits in.
    e.stopPropagation();
    onClose();
  };
  return (
    <div className={styles.picker} data-testid={`${kind}-picker`}>
      <input
        type="search"
        className={styles.pickerSearch}
        data-testid={`${kind}-search`}
        aria-label={`Search ${kind}s`}
        placeholder={`Search ${api.entries.length} ${kind}s, or type a new one`}
        // The user just opened this box to type in it.
        // eslint-disable-next-line jsx-a11y/no-autofocus -- a field the user just opened (code-conventions)
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <div className={styles.pickerList}>
        {groups.map(([group, entries]) => (
          <Group key={group} {...{ kind, character, onChange, group, entries, taken }} />
        ))}
        {needle && !known && (
          <button type="button" className={styles.pickerRow} data-testid={`${kind}-add-custom`} onClick={addCustom}>
            <span className={styles.pickerName}>
              Write down &quot;{query.trim()}&quot; as a custom {kind}
            </span>
            <span className={styles.pickerDesc}>A name on the sheet and in exports. No rules effect.</span>
          </button>
        )}
      </div>
      <button type="button" className={styles.pickerClose} onClick={onClose}>
        DONE
      </button>
    </div>
  );
}
